import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { DEMO } from '../../lib/demo';
import { Bot, ArrowRightLeft, CheckCircle2, Copy, KeyRound, RefreshCw, Flame, Rocket, ChevronDown, SlidersHorizontal, BarChart3, AlertTriangle } from 'lucide-react';
import BotPanel from './BotPanel';
import BotVue from './BotVue';
import RapportBot from './RapportBot';
import BilanBot from './BilanBot';

// Page « Bot Telegram » — statut RÉEL de l'intégration bot HERA ↔ ChatHub.
// Le bot (app séparée) accueille/trie les fans et, quand un fan devient chaud
// ou paie, envoie un webhook signé ici : ChatHub tague la conv « 🔥 Lead chaud (bot) »
// et la remonte en haut de l'inbox. Cette page montre l'état de ce lien + le flux
// des handoffs reçus. La persona / le funnel / le mode se règlent dans le panneau du bot.

const BOT_USERNAME = '@lenachatmodelbot';

function Copyable({ value, label }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    try { navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div>
      {label && <div className="text-[11px] text-slate-500 mb-1">{label}</div>}
      <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
        <code className="text-[12px] text-slate-300 truncate flex-1">{value}</code>
        <button onClick={copy} className="shrink-0 text-slate-400 hover:text-white transition-colors" title="Copier">
          {done ? <CheckCircle2 size={15} className="text-emerald-400" /> : <Copy size={15} />}
        </button>
      </div>
    </div>
  );
}

const DEMO_CFG = { enabled: true, has_secret: true, webhook_url: 'https://api.chat-hubs.com/api/webhooks/hera', agency_id: 'demo-agency', events_received: 128, events_matched: 110 };
const DEMO_EVENTS = [
  { event: 'sale', name: '@marc_l', status: 'PAID', matched: true, at: new Date(Date.now() - 3600e3).toISOString(), conversation_id: 'x' },
  { event: 'handoff', name: '@julien22', status: 'HOT', matched: true, at: new Date(Date.now() - 8400e3).toISOString(), conversation_id: 'x' },
];
const DEMO_STATS = { days: 7, events_total: 34, handoffs: 21, sales_signaled: 9, new_fans: 34, fans_touched: 30, matched: 28, match_rate: 82, ca_generated: 1240, nb_ventes: 11, active_24h: true, last_event_at: new Date().toISOString() };

function Kpi({ label, value, sub, color }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[11px] text-slate-400">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// Une ligne de la check-list d'activation : voyant ✓ (fait), ✗ (à faire) ou point (info).
function Check({ ok, warn, n, title, children }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 mt-0.5">
        {ok ? <CheckCircle2 size={20} className="text-emerald-400" />
          : warn ? <AlertTriangle size={20} className="text-amber-400" />
          : <span className="w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-200 text-[11px] font-bold flex items-center justify-center">{n}</span>}
      </span>
      <div className="pt-0.5 flex-1 min-w-0">
        <div className={`text-sm font-semibold mb-0.5 ${ok ? 'text-emerald-200' : 'text-white'}`}>{title}</div>
        <div className="text-[13px] text-slate-300 leading-relaxed space-y-2">{children}</div>
      </div>
    </li>
  );
}

const EVENT_META = {
  sale: { label: 'Vente → repris par un chatteur', color: '#37d39a', Icon: CheckCircle2 },
  handoff: { label: 'Lead chaud → passé au chatteur', color: '#ff8a5b', Icon: Flame },
  hot: { label: 'Lead chaud → passé au chatteur', color: '#ff8a5b', Icon: Flame },
  hot_lead: { label: 'Lead chaud → passé au chatteur', color: '#ff8a5b', Icon: Flame },
  new_fan: { label: 'Nouveau fan accueilli par le bot', color: '#73a6ff', Icon: Bot },
};

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

export default function BotConfig() {
  const [cfg, setCfg] = useState(DEMO ? DEMO_CFG : null);
  const [events, setEvents] = useState(DEMO ? DEMO_EVENTS : []);
  const [loaded, setLoaded] = useState(DEMO);
  const [rotating, setRotating] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [panelOn, setPanelOn] = useState(false);
  const [stats, setStats] = useState(DEMO ? DEMO_STATS : null);
  const [days, setDays] = useState(7);
  const [couts, setCouts] = useState(null);
  // 21/09 : le modele choisi dans la vue d'ensemble ouvre ses reglages juste en dessous
  const [modeleChoisi, setModeleChoisi] = useState('');
  const [voirInstall, setVoirInstall] = useState(false);

  const loadStats = useCallback((d) => {
    if (DEMO) return;
    api.get(`/hera/stats?days=${d}`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  useEffect(() => { loadStats(days); }, [days, loadStats]);

  // 30/09 (Marlon : « j'aurais besoin des coûts ») — le coût du bot n'existait nulle part, parce
  // qu'il jetait le bloc `usage` de chaque appel. Il compte maintenant, et on l'affiche en haut de
  // la page. Un échec ne casse rien : la carte disparaît, le reste de la page vit sa vie.
  useEffect(() => {
    if (DEMO) return;
    api.get('/hera/couts').then((r) => setCouts(r.data)).catch(() => setCouts(null));
  }, []);

  const load = useCallback(() => {
    if (DEMO) return;
    Promise.all([
      api.get('/hera/config').then((r) => r.data).catch(() => null),
      api.get('/hera/events?limit=25').then((r) => r.data?.events || []).catch(() => []),
      api.get('/bot-panel/_status').then((r) => !!r.data?.available).catch(() => false),
    ]).then(([c, e, p]) => { setCfg(c); setEvents(e); setPanelOn(p); }).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    if (DEMO) return;
    const first = !cfg?.has_secret;
    if (!first && !window.confirm(
      "⚠️ Régénérer la clé va COUPER le bot actuel jusqu'à ce que tu colles la nouvelle clé dans ses réglages Railway (CHATHUB_SECRET). Continuer ?")) return;
    setRotating(true);
    try {
      const r = await api.post('/hera/config/rotate-secret', {});
      setNewSecret(r.data?.secret || '');
      setShowAdvanced(true);
      load();
    } catch { /* ignore */ } finally { setRotating(false); }
  };

  const connected = !!cfg?.has_secret;
  const received = cfg?.events_received || 0;
  const matched = cfg?.events_matched || 0;
  const rate = received ? Math.round((matched / received) * 100) : 0;
  const live = received > 0 || !!stats?.active_24h; // le bot a réellement traité des fans

  if (!loaded) return <div className="p-6 text-slate-400">Chargement…</div>;

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="bot-page">
      <h1 className="text-2xl font-bold text-white flex items-center gap-3">Bot Telegram
        {connected
          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 size={12} /> Connecté</span>
          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 border border-white/10">Non connecté</span>}
      </h1>
      <p className="text-sm text-slate-400 mb-5">Allume ou éteins le bot par modèle, vois avec qui il parle en ce moment, et ouvre n'importe quelle conversation en un clic. Les réglages détaillés sont juste en dessous.</p>

      {/* 14/10 (Marlon : « le rapport … je pourrais voir ça directement tout en haut ») */}
      {!DEMO && <BilanBot />}
      {!DEMO && <RapportBot />}

      {/* 30/09 : CE QUE LE BOT COÛTE. Estimation depuis les jetons réellement consommés ;
          l'avertissement qui vient du bot est affiché tel quel, pour qu'on ne prenne pas
          ça pour une facture. */}
      {couts && !couts.indisponible && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4">
          <h3 className="font-semibold text-white mb-3">Ce que le bot te coûte</h3>
          <div className="grid grid-cols-3 gap-3">
            {[['Aujourd’hui', couts.aujourdhui], ['7 jours', couts.sept_jours], ['30 jours', couts.trente_jours]].map(([lib, v]) => (
              <div key={lib} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[11px] text-slate-400">{lib}</div>
                <div className="text-2xl font-bold mt-1 text-white">{(v?.total ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $</div>
                <div className="text-[11px] text-slate-500">{v?.appels ?? 0} appel{(v?.appels ?? 0) > 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
          {Object.keys(couts.par_modele || {}).length > 0 && (
            <div className="mt-3 text-[12px] text-slate-400">
              Aujourd’hui par moteur :{' '}
              {Object.entries(couts.par_modele).map(([m, v], i) => (
                <span key={m}>{i ? ' · ' : ''}<b className="text-slate-300">{m}</b> {(v.total || 0).toFixed(2)} $ ({v.appels} appel{v.appels > 1 ? 's' : ''})</span>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-2">{couts.avertissement}</p>
        </div>
      )}

      {/* 21/09 : VUE D'ENSEMBLE — allume / eteint, pret ou pas, avec qui il parle, ouvrir la conv */}
      <BotVue onChoisirModele={(id) => {
        setModeleChoisi(id);
        setTimeout(() => { const el = document.getElementById('panneau-bot'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
      }} />

      {/* clé fraîchement générée (une seule fois) */}
      {newSecret && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 mb-4">
          <div className="flex items-center gap-2 text-amber-200 font-semibold mb-2"><KeyRound size={16} /> Ta clé de connexion (affichée une seule fois)</div>
          <p className="text-[13px] text-amber-100/80 mb-3">Colle-la dans le bot (Railway → variable <code className="text-amber-200">CHATHUB_SECRET</code>), avec l'ID d'agence ci-dessous. Ferme cette page et elle disparaît.</p>
          <Copyable value={newSecret} label="CHATHUB_SECRET" />
        </div>
      )}

      {/* Panneau du bot INTÉGRÉ (par modèle) — l'endroit où tu règles tout */}
      {panelOn ? (
        <div id="panneau-bot" className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal size={16} className="text-indigo-400" />
            <h2 className="text-lg font-bold text-white">Régler un modèle</h2>
          </div>
          <BotPanel modeleImpose={modeleChoisi} />
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-5 mb-4 text-[13px] text-amber-100/90">
          {DEMO ? 'Réglages détaillés non disponibles en mode démo.' : 'Les réglages du bot sont momentanément indisponibles (connexion au bot). Réessaie dans un instant.'}
        </div>
      )}

      {/* 21/09 : tout ce qui ne sert qu'une fois, replie en bas pour ne plus encombrer */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mt-6">
        <button onClick={() => setVoirInstall((v) => !v)} className="w-full flex items-center justify-between text-left">
          <h3 className="font-semibold text-white flex items-center gap-2"><KeyRound size={16} className="text-slate-400" /> Installation, fonctionnement et historique technique</h3>
          <span className="text-slate-500 text-sm">{voirInstall ? 'masquer' : 'afficher'}</span>
        </button>
        <p className="text-[12px] text-slate-500 mt-1">Tu n'en as besoin qu'une fois, pour relier un nouveau modèle ou dépanner.</p>
        {/* 30/09 (Marlon) : « est-ce que les infos sont bonnes-là ? je sais pas ». Elles sont exactes,
            mais elles ne mesurent QUE le mode « accueil » : en A→Z le bot ne passe jamais la main,
            donc tout y reste à zéro — et un zéro sans explication se lit comme une panne. On le dit. */}
        {/* 30/09 : mon avertissement précédent disait « tout y restera à 0 — c'est normal ». C'était
            FAUX : la carte affichait 443 €, 20 ventes et 48 fans touchés. Ce qui reste à zéro, ce sont
            les deux compteurs de passage (en A→Z le bot ne passe jamais la main), pas les autres. */}
        <p className="text-[12px] text-slate-400 mt-1">
          <b className="text-slate-300">Passés à une chatteuse</b> et <b className="text-slate-300">Ventes signalées</b> restent
          à <b>0</b> tant que tes modèles sont en <b>A→Z</b> : le bot ne passe jamais la main. C'est normal, pas une panne.
          Les deux autres chiffres, eux, comptent vraiment.
        </p>
      </div>
      {voirInstall && (<>
      {/* 21/09 : ces stats comptent les « passages a une chatteuse » (mode accueil). En A→Z il n'y en a
          jamais : les vrais resultats du bot sont maintenant sous chaque modele, en haut de la page. */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4 mt-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-white flex items-center gap-2"><BarChart3 size={16} className="text-indigo-400" /> Passages à une chatteuse <span className="text-[11px] font-normal text-amber-300/80">— mode « accueil » uniquement</span></h3>
          <div className="flex items-center gap-1 rounded-lg border border-white/10 p-0.5">
            {[[7, '7 j'], [30, '30 j'], [0, 'Tout']].map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)} className={`px-2.5 py-1 text-xs rounded-md transition-colors ${days === d ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
        </div>
        {stats ? (
          stats.events_total === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              Le bot n'a encore rien traité sur cette période.<br />Dès qu'il accueille des fans et passe des leads, ses stats (CA généré, ventes, leads chauds, fans touchés) s'afficheront ici.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* 30/09 : ce chiffre valait 443,20 € alors que le bot n'avait encaissé que 18 €. Il prenait
                    les ventes de TOUTE conversation où le bot avait envoyé un événement — or 266 des 306 sont
                    ses notes automatiques : il s'attribuait le travail des chatteuses. Le libellé dit
                    maintenant ce qui est compté, et combien de ventes antérieures sont écartées. */}
                {/* 14/10 (C-505) : « une vente faite par un chatter appartient au chatter ». Ce chiffre ne compte
                    plus que les ventes signées « 🤖 Bot HERA » ; celles des chatteuses sur ces fans sont dites à part. */}
                <Kpi label="Vendu par le bot (signé 🤖 Bot HERA)"
                     value={`${(stats.ca_generated || 0).toLocaleString('fr-FR')} €`}
                     sub={`${stats.nb_ventes} vente${stats.nb_ventes > 1 ? 's' : ''}${stats.ventes_equipe_zone_bot ? ` · + ${stats.ventes_equipe_zone_bot} de chatteuses sur ces fans (${(stats.ca_equipe_zone_bot || 0).toLocaleString('fr-FR')} €, à elles)` : ''}`}
                     color="#37d39a" />
                <Kpi label="Passés à une chatteuse" value={stats.handoffs} sub="fans chauds repris à la main" color="#ff8a5b" />
                <Kpi label="Fans touchés" value={stats.fans_touched} sub="gérés par le bot" color="#73a6ff" />
                <Kpi label="Ventes signalées" value={stats.sales_signaled} sub="1ʳᵉ vente du bot" color="#a78bfa" />
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Vendu par le bot = ventes signées « 🤖 Bot HERA » ; une vente faite par une chatteuse reste à la chatteuse · {stats.convs_tenues ?? 0} conversation{(stats.convs_tenues ?? 0) > 1 ? 's' : ''} en zone bot · {stats.matched}/{stats.events_total} événements rattachés à une conv · {stats.active_24h ? 'bot actif (événement < 24 h)' : 'aucun événement depuis 24 h'}.
              </p>
            </>
          )
        ) : (
          // Repli tant que le calcul détaillé n'est pas déployé : compteurs d'intégration.
          <div>
            <div className="grid grid-cols-3 gap-3">
              {[['Événements reçus du bot', received, '#3f7dff'], ['Rattachés à une conv', matched, '#37d39a'], ['Taux de rattachement', received ? rate + ' %' : '—', '#73a6ff']].map((k) => (
                <div key={k[0]} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-[11px] text-slate-400">{k[0]}</div>
                  <div className="text-2xl font-bold mt-1" style={{ color: k[2] }}>{k[1]}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Stats détaillées (CA, ventes…) disponibles après la prochaine mise à jour.</p>
          </div>
        )}
      </div>

      {/* comment ça marche */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2 mb-4"><ArrowRightLeft size={16} className="text-indigo-400" /> Comment ça marche</h3>
        <ol className="grid md:grid-cols-3 gap-3">
          {[
            ['Le bot accueille & trie', 'Il répond aux nouveaux fans 24/7 avec la persona du modèle et écarte les curieux.'],
            ['Il détecte le bon moment', "Quand le fan devient chaud ou paie sa 1ʳᵉ offre, le bot passe la main."],
            ['La conv remonte chez toi', "Elle est taguée 🔥 Lead chaud (bot) et remonte en haut de l'Inbox — le chatteur enchaîne."],
          ].map(([t, d], i) => (
            <li key={t} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500/15 text-indigo-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <div><div className="text-sm text-white font-medium">{t}</div><div className="text-[13px] text-slate-400">{d}</div></div>
            </li>
          ))}
        </ol>
      </div>

      {/* flux des handoffs */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white flex items-center gap-2"><Flame size={16} className="text-orange-400" /> Fans passés à une chatteuse</h3>
          <button onClick={load} className="text-slate-400 hover:text-white transition-colors" title="Rafraîchir"><RefreshCw size={15} /></button>
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            Aucun pour l'instant. Ça n'arrive qu'en mode « Accueil + 1ʳᵉ vente, puis une chatteuse » : en mode A→Z, le bot garde la main et cette liste reste vide — c'est normal.
          </div>
        ) : (
          <div className="space-y-1">
            {events.map((e, i) => {
              const m = EVENT_META[e.event] || { label: e.event, color: '#94a3b8', Icon: Bot };
              const Ic = m.Icon;
              return (
                <div key={i} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.color + '22' }}>
                    <Ic size={15} style={{ color: m.color }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{e.name || 'Fan'} <span className="text-slate-500">— {m.label}</span></div>
                    <div className="text-[11px] text-slate-500">{timeAgo(e.at)}{!e.matched && ' · non rattaché (conv pas encore synchro)'}</div>
                  </div>
                  {e.matched
                    ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 shrink-0">dans l'inbox</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-slate-400 shrink-0">en attente</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* check-list d'activation EN DIRECT */}
      <div className="rounded-2xl border border-indigo-500/25 bg-indigo-500/[0.05] p-5 mb-4">
        <button onClick={() => setShowGuide((v) => !v)} className="w-full flex items-center justify-between text-left">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Rocket size={16} className="text-indigo-300" /> Activer le bot — check-list
            {live
              ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">en marche</span>
              : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-200 border border-amber-500/30">pas encore actif</span>}
          </h3>
          <ChevronDown size={18} className={`text-slate-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
        </button>
        {showGuide && (
          <ol className="mt-4 space-y-4">
            <Check n={1} ok={connected} title="Générer la clé de connexion">
              {connected
                ? <span className="text-emerald-300">✓ Clé générée. Elle est déjà en place côté ChatHub.</span>
                : <div>Clique pour créer la clé qui relie le bot à ChatHub.
                    <div className="mt-2"><button onClick={rotate} disabled={rotating} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60"><KeyRound size={13} className="inline mr-1" />{rotating ? 'Génération…' : 'Connecter le bot'}</button></div>
                  </div>}
            </Check>
            <Check n={2} ok={live} title="Coller ces 3 valeurs dans le bot (Railway)">
              <div>Dans les variables Railway du bot, colle :</div>
              {newSecret
                ? <Copyable value={newSecret} label="CHATHUB_SECRET (affichée une seule fois)" />
                : <div className="text-[12px] text-slate-400">• <code className="text-indigo-300">CHATHUB_SECRET</code> = la clé (régénère-la ci-dessous si tu l'as perdue)</div>}
              <Copyable value={cfg?.agency_id || ''} label="CHATHUB_AGENCY_ID" />
              <Copyable value={cfg?.webhook_url || 'https://api.chat-hubs.com/api/webhooks/hera'} label="CHATHUB_WEBHOOK_URL" />
            </Check>
            <Check n={3} ok={panelOn} warn={!panelOn} title="Panneau du bot joignable">
              {panelOn
                ? <span className="text-emerald-300">✓ ChatHub parle au bot — les réglages par modèle sont dispo ci-dessous.</span>
                : <span>⚠ ChatHub n'arrive pas à joindre le bot. Vérifie côté Railway ChatHub la variable <code className="text-amber-200">HERA_BOT_PASSWORD</code> (et que le bot tourne).</span>}
            </Check>
            <Check n={4} ok={live} title="Relier le Telegram Business de chaque modèle">
              Sur le téléphone du modèle (Telegram <b className="text-white">Premium</b>) : <b className="text-white">Réglages → Telegram Business → Chatbots</b>, ajoute <code className="text-indigo-300">{BOT_USERNAME}</code> et <b className="text-white">autorise-le à répondre</b>. Le statut par modèle (✓ relié / ⚠ pas relié) s'affiche dans les réglages ci-dessous.
            </Check>
            <Check n={5} ok={live} warn={connected && !live} title="Le bot reçoit les fans">
              {live
                ? <span className="text-emerald-300">✓ Le bot tourne — {received} événement{received > 1 ? 's' : ''} reçu{received > 1 ? 's' : ''}.</span>
                : <span>En attente du <b className="text-white">1ᵉʳ message d'un fan</b>. Dès que le bot répond à quelqu'un, ça passe au vert ici et les stats se remplissent. (Astuce : garde un numéro dans la <b className="text-white">whitelist de test</b> pour essayer sans ouvrir à tous.)</span>}
            </Check>
          </ol>
        )}
      </div>

      {/* avancé : webhook + clé */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mt-4">
        <button onClick={() => setShowAdvanced((v) => !v)} className="w-full flex items-center justify-between text-left">
          <h3 className="font-semibold text-white flex items-center gap-2"><KeyRound size={16} className="text-slate-400" /> Détails techniques</h3>
          <span className="text-slate-500 text-sm">{showAdvanced ? 'masquer' : 'afficher'}</span>
        </button>
        {showAdvanced && (
          <div className="mt-4 space-y-3">
            <Copyable value={cfg?.webhook_url || 'https://api.chat-hubs.com/api/webhooks/hera'} label="URL du webhook (dans le bot : CHATHUB_WEBHOOK_URL)" />
            <Copyable value={cfg?.agency_id || ''} label="ID d'agence (dans le bot : CHATHUB_AGENCY_ID)" />
            <div className="pt-2">
              <button onClick={rotate} disabled={rotating}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white border border-red-500/30 bg-red-500/10 hover:bg-red-500/15 transition-colors disabled:opacity-60">
                <RefreshCw size={15} /> {rotating ? '…' : 'Régénérer la clé'}
              </button>
              <p className="text-[11px] text-slate-500 mt-2">Coupe le bot actuel jusqu'à ce que tu colles la nouvelle clé dans Railway. À n'utiliser que si la clé a fuité.</p>
            </div>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
